import React, { useState, useContext } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import useAuth from '../hooks/useAuth';
import { AuthContext } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';

const drawerWidth = 270;

const ROUTE_PERMISSIONS = {
  '/': 'reports.revenue',
  '/users': 'users.read',
  '/astrologers': 'astrologers.read',
  '/astrologers/kyc': 'astrologers.kyc',
  '/astrologers/register': 'astrologers.write',
  '/astrologers/invite': 'astrologers.write',
  '/astrologer-inquiries': 'astrologers.read',
  '/chat-logs': 'support.read',
  '/call-logs': 'support.read',
  '/transactions': 'finance.commissions',
  '/wallet/coins': 'finance.commissions',
  '/shop/products': 'shop.read',
  '/shop/orders': 'shop.read',
  '/shop/coupons': 'shop.read',
  '/categories': 'shop.read',
  '/blogs': 'shop.read',
  '/courses': 'shop.read',
  '/commissions': 'finance.commissions',
  '/reports/revenue': 'reports.revenue',
  '/reports/gst': 'reports.gst',
  '/reports/tds': 'reports.tds',
  '/pooja-types': 'shop.read',
  '/pooja-orders': 'shop.read',
  '/payouts': 'finance.payouts',
  '/live-streams': 'astrologers.read',
  '/support/tickets': 'support.read',
  '/settings': 'settings.read',
  '/settings/payment': 'settings.read',
  '/settings/currency': 'settings.read',
  '/settings/integrations': 'settings.read',
  '/settings/notifications': 'settings.read',
};

const AdminLayout = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const { hasPermission } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const requiredPerm = ROUTE_PERMISSIONS[location.pathname];
  if (requiredPerm && !hasPermission(requiredPerm)) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Access Denied</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You do not have permission to access this page.
          </Typography>
          <Button variant="contained" onClick={() => window.history.back()}>Go Back</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Topbar onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, md: 3 },
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
